import { Button } from '@mdvip/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@mdvip/shared/components/ui/dialog';
import { useTranslation } from 'react-i18next';
import type { ShowDialogEvent } from './types';

interface PtlinkedDialogProps {
  /** The vendor's current dialog request; `null` closes it. Same controlled-state shape as
   * this repo's other data-driven dialogs (e.g. `CancelPaymentDialog`'s `payment` prop). */
  event: ShowDialogEvent | null;
  onClose: () => void;
}

/**
 * Renders `onShowDialog` events (see ptlinked.d.ts) through this repo's own Dialog primitive
 * instead of the vendor's jQuery UI dialog — that's the whole point of `dialog_box_type:
 * 'hook'`. `content` is rendered as plain text, not HTML, even though the vendor's own jQuery
 * dialog mode does `.html(content)` internally: every string we've actually seen it send is
 * plain text, and rendering arbitrary third-party content as HTML is a risk not worth taking
 * for what's currently just confirmation copy. Revisit if PTLinked ever sends real markup.
 */
export function PtlinkedDialog({ event, onClose }: PtlinkedDialogProps) {
  const { t } = useTranslation('shared');
  const isConfirmation = !!(event?.confirmation_btn && event?.confirmation_callbackn);

  const handleConfirm = () => {
    event?.confirmation_callbackn?.();
    onClose();
  };

  return (
    <Dialog open={event !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{event?.title}</DialogTitle>
          <DialogDescription>{event?.content}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          {isConfirmation ? (
            <>
              <Button variant="outline" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              {/* The only confirm/cancel dialog the vendor's source actually sends today is
                  "remove this favorite" — destructive styling matches that, not a guess. If a
                  future non-destructive confirmation shows up, this'll need a tone signal we
                  don't have yet (the vendor's payload has no such field). */}
              <Button variant="destructive" onClick={handleConfirm}>
                {event?.confirmation_btn}
              </Button>
            </>
          ) : (
            <Button onClick={onClose}>{t('common.close')}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
